import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styles from './login.module.css';
import { useTranslation } from 'react-i18next';
import eyeIcon from '../../assets/eye-show-svgrepo-com.svg';
import eyeSlashIcon from '../../assets/eye-off-svgrepo-com.svg';
import { Button, useNotification } from '../../components';
import { AuthService } from '../../services';
import { CONFIRMATION_TOKEN_KEY, NavigationPaths, TOKEN_KEY } from '../../constants';
import { setAuthData } from '../../reducers/authReducer';

export const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { addNotification } = useNotification();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    localStorage.removeItem('setNewPassword');
  }, []);

  useEffect(() => {
    document.body.classList.add(styles['body-overflow-hidden']);

    return () => {
      document.body.classList.remove(styles['body-overflow-hidden']);
    };
  }, []);

  const isValidEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const handleCodeSubmit = async () => {
    if (email && password) {
      if (!isValidEmail(email)) {
        addNotification({
          title: t('error'),
          message: t('enterValidEmail'),
          type: 'error',
        });
        return;
      }
      const { data: body } = await AuthService.auth(email, password);
      const session = body?.data;
      const accessToken = session?.tokenData?.accessToken as string | undefined;
      if (!accessToken) {
        return;
      }
      if (session?.['2fa']) {
        localStorage.setItem(CONFIRMATION_TOKEN_KEY, accessToken);
        localStorage.setItem('pass', password);
        return navigate(`${NavigationPaths.CONFIRMATION}?email=${encodeURIComponent(email)}`);
      }
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.removeItem('pass');
      dispatch(setAuthData());
      return navigate(NavigationPaths.ORGANISATION_USERS);
    }
    addNotification({
      title: t('error'),
      message: t('fieldNotBeEmpty'),
      type: 'error',
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCodeSubmit();
    }
  };

  return (
    <div className={styles['login-container']}>
      <div className={styles['login-box']}>
        <h1 className={styles['login-title']}>{t('signIn')}</h1>
        <input
          type="email"
          placeholder={t('email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyPress}
          className={styles['input']}
        />
        <div className={styles['password-container']}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder={t('password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={styles['input']}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={styles['toggle-password']}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <img
              src={showPassword ? eyeSlashIcon : eyeIcon}
              alt={showPassword ? 'Hide password' : 'Show password'}
              className={styles['toggle-icon']}
            />
          </button>
        </div>
        <a
          href="#"
          className={styles['forgot-password']}
          onClick={(e) => {
            e.preventDefault();
            navigate(NavigationPaths.RESET_PASSWORD);
          }}
        >
          {t('forgotPassword')}
        </a>
        <Button
          onClick={handleCodeSubmit}
          variant="primary"
          showArrow
          width="100%"
        >
          {t('signIn')}
        </Button>
      </div>
    </div>
  );
};
